import { lazy, Suspense } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { store } from '@/store/store';
import { selectIsAuthenticated, selectIsLoading } from '@/store/authSlice';
import { AuthBootstrap } from '@/components/AuthBootstrap';
import { Spinner } from '@/ui';
import { DEFAULT_SETTINGS_TAB } from '@/components/settings/settingsTabs';
import * as layout from '@/styles/layout.css';

// Public / first-paint routes stay eagerly bundled: they are what an
// unauthenticated visitor hits first, so deferring them would only add a
// network round-trip before the login screen can render.
import LoginPage from '@/pages/LoginPage';
import SsoCallbackPage from '@/pages/SsoCallbackPage';
import MagicLinkPage from '@/pages/MagicLinkPage';
import AuthErrorPage from '@/pages/AuthErrorPage';

// Authenticated app is code-split per route. Each page (and the libraries only
// it pulls in — the thesis editor, pitch-deck viewer, settings, admin) loads on
// demand instead of riding along in the initial bundle the login page ships.
const AppShell = lazy(() =>
  import('@/layout/AppShell/AppShell').then((m) => ({ default: m.AppShell })),
);
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const DealflowPage = lazy(() => import('@/pages/DealflowPage'));
const AdvancedSearchPage = lazy(() => import('@/pages/AdvancedSearchPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const InvestmentThesisPage = lazy(() => import('@/pages/InvestmentThesisPage'));
const PitchDeckDetailPage = lazy(() => import('@/pages/PitchDeckDetailPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const AdminLayout = lazy(() => import('@/pages/AdminLayout'));
const DemoVcPublicUploadPage = lazy(() => import('@/pages/demo-vc/DemoVcPublicUploadPage'));
const DueDiligencePage = lazy(() => import('@/pages/DueDiligencePage'));
const CooperationPage = lazy(() => import('@/pages/CooperationPage'));
const HelpPage = lazy(() => import('@/pages/HelpPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function ProtectedRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  if (isLoading) return <Centered><Spinner size="md" /></Centered>;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <Outlet />;
}

function PublicRoute() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isLoading = useSelector(selectIsLoading);
  if (isLoading) return <Centered><Spinner size="md" /></Centered>;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className={layout.centerBoth} style={{ minHeight: '100vh' }}>
      {children}
    </div>
  );
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <AuthBootstrap>
      <Suspense fallback={<Centered><Spinner size="md" /></Centered>}>
      <Routes>
        {/* Public per-tenant upload pages — one explicit route per tenant */}
        <Route path="/demo-vc/upload" element={<DemoVcPublicUploadPage />} />

        {/* Auth failure — reachable regardless of session state (an already-
            signed-in user clicking an expired magic link still needs to see why). */}
        <Route path="/auth/error" element={<AuthErrorPage />} />

        {/* Public legal / contact pages — reachable from the Footer regardless
            of session state. */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Public landing */}
        <Route element={<PublicRoute />}>
          <Route path="/" element={<LoginPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/auth/magic" element={<MagicLinkPage />} />
          <Route path="/auth/complete" element={<SsoCallbackPage />} />
        </Route>

        {/* Authenticated app */}
        <Route element={<ProtectedRoute />}>
          {/* AppShell derives the topbar breadcrumb from the pathname via
              navConfig.titleKeyForPath (this app uses a declarative router, which
              has no route-`handle`/useMatches support). */}
          <Route element={<AppShell />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dealflow" element={<DealflowPage />} />
            <Route path="/advanced-search" element={<AdvancedSearchPage />} />
            <Route path="/cooperation" element={<CooperationPage />} />
            <Route path="/due-diligence" element={<DueDiligencePage />} />
            <Route path="/help" element={<HelpPage />} />
            {/* Profile is now a Settings tab; keep the old URL working. */}
            <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
            <Route path="/settings" element={<Navigate to={`/settings/${DEFAULT_SETTINGS_TAB}`} replace />} />
            <Route path="/settings/:tab" element={<SettingsPage />} />
            <Route path="/thesis" element={<Navigate to="/thesis/stage" replace />} />
            <Route path="/thesis/:tab" element={<InvestmentThesisPage />} />
            <Route path="/pitchdecks/:id" element={<PitchDeckDetailPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </AuthBootstrap>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <AppRoutes />
      {/* Offset clears the 64px topbar so toasts no longer cover the search box. */}
      <Toaster position="top-right" richColors closeButton offset={{ top: 80, right: 24 }} />
    </Provider>
  );
}
